import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import type { DashboardIncident } from "@/contracts";

import {
  dashboardSummaryQuery,
  devicesQuery,
  loanAgreementsQuery,
  serviceAgreementsQuery,
} from "@/api/queries";
import { hasAnyView, useAuth } from "@/auth/auth-context";
import { useApiClient } from "@/lib/api-context";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import { IncidentStatusBadge, SeverityBadge } from "@/features/devices/device-badges";
import {
  DEVICE_TABLE_COLUMNS,
  formatIncidentValue,
  INCIDENT_TYPE_LABELS,
} from "@/features/devices/device-labels";
import { DeviceRows } from "@/features/devices/device-rows";
import { formatDateTime, formatRelative } from "@/lib/format";
import { KpiCard } from "./kpi-card";
import {
  IconAlertTriangle,
  IconDevices,
  IconFileText,
  IconPlugConnectedX,
  IconTemperature,
  IconWifi,
  IconWifiOff,
} from "@tabler/icons-react";

export function DashboardPage() {
  const { t } = useTranslation("pages");
  const client = useApiClient();
  const { user } = useAuth();
  const showCommercial = hasAnyView(user, ["LOAN_AGREEMENTS", "SERVICE_AGREEMENTS"]);
  const summary = useQuery(dashboardSummaryQuery(client));
  // The table under the cards is the same device list, newest activity
  // first, capped to what fits on a screen.
  const devices = useQuery(devicesQuery(client, { page: 1 }));
  const loanDrafts = useQuery({
    ...loanAgreementsQuery(client, { page: 1, status: "DRAFT" }),
    enabled: showCommercial && hasAnyView(user, ["LOAN_AGREEMENTS"]),
  });
  const serviceDrafts = useQuery({
    ...serviceAgreementsQuery(client, { page: 1, status: "DRAFT" }),
    enabled: showCommercial && hasAnyView(user, ["SERVICE_AGREEMENTS"]),
  });
  const loansEnding = useQuery({
    ...loanAgreementsQuery(client, { page: 1, endingWithinDays: 30 }),
    enabled: showCommercial && hasAnyView(user, ["LOAN_AGREEMENTS"]),
  });
  const servicesEnding = useQuery({
    ...serviceAgreementsQuery(client, { page: 1, endingWithinDays: 30 }),
    enabled: showCommercial && hasAnyView(user, ["SERVICE_AGREEMENTS"]),
  });

  return (
    <>
      <PageHeader
        title={t("dashboardTitle")}
        description={
          summary.data
            ? t("dashboardUpdated", { when: formatRelative(summary.data.generatedAt) })
            : t("dashboardFallback")
        }
      />

      {summary.isPending ? <LoadingBlock label={t("loadingIndicators")} /> : null}

      {summary.isError ? (
        <ErrorBlock error={summary.error} onRetry={() => void summary.refetch()} />
      ) : null}

      {summary.data ? (
        <>
          <section
            aria-label={t("indicators")}
            className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6"
          >
            <KpiCard
              label={t("kpiDevices")}
              value={summary.data.kpis.devicesTotal}
              icon={IconDevices}
              to="/devices"
            />
            <KpiCard
              label={t("kpiOnline")}
              value={summary.data.kpis.devicesOnline}
              emphasis="ok"
              icon={IconWifi}
              to="/devices?connectivity=ONLINE"
            />
            <KpiCard
              label={t("kpiNotReporting")}
              value={summary.data.kpis.devicesOffline}
              emphasis={summary.data.kpis.devicesOffline > 0 ? "danger" : "neutral"}
              icon={IconWifiOff}
              hint={t("kpiOfflineHint", { minutes: summary.data.offlineAfterMinutes })}
              to="/devices?connectivity=OFFLINE"
            />
            <KpiCard
              label={t("kpiNeverSeen")}
              value={summary.data.kpis.devicesNeverSeen}
              emphasis={summary.data.kpis.devicesNeverSeen > 0 ? "warn" : "neutral"}
              icon={IconPlugConnectedX}
              hint={t("kpiNeverSeenHint")}
              to="/devices?connectivity=NEVER_SEEN"
            />
            <KpiCard
              label={t("kpiOutOfRange")}
              value={summary.data.kpis.devicesOutOfRange}
              emphasis={summary.data.kpis.devicesOutOfRange > 0 ? "danger" : "neutral"}
              icon={IconTemperature}
              hint={t("kpiLastReadingHint")}
            />
            <KpiCard
              label={t("kpiOpenIncidents")}
              value={summary.data.kpis.incidentsOpen}
              emphasis={summary.data.kpis.incidentsOpen > 0 ? "warn" : "neutral"}
              icon={IconAlertTriangle}
              hint={t("kpiIncidents24h", { count: summary.data.kpis.incidentsLast24h })}
              to="/incidents"
            />
          </section>

          <div className="mt-6">
            <RecentIncidents incidents={summary.data.recentIncidents} />
          </div>
        </>
      ) : null}

      {showCommercial ? (
        <section aria-label={t("commercialSection")} className="mt-6">
          <h2 className="mb-3 text-sm font-semibold text-ink">{t("commercialSection")}</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {hasAnyView(user, ["LOAN_AGREEMENTS"]) ? (
              <>
                <KpiCard
                  label={t("kpiLoanDrafts")}
                  value={loanDrafts.data?.pagination.total ?? 0}
                  emphasis={
                    (loanDrafts.data?.pagination.total ?? 0) > 0 ? "warn" : "neutral"
                  }
                  icon={IconFileText}
                  to="/contracts/loans?status=DRAFT"
                />
                <KpiCard
                  label={t("kpiLoansEnding")}
                  value={loansEnding.data?.pagination.total ?? 0}
                  emphasis={
                    (loansEnding.data?.pagination.total ?? 0) > 0 ? "warn" : "neutral"
                  }
                  hint={t("kpiEndingHint")}
                  icon={IconFileText}
                  to="/contracts/loans?endingWithinDays=30"
                />
              </>
            ) : null}
            {hasAnyView(user, ["SERVICE_AGREEMENTS"]) ? (
              <>
                <KpiCard
                  label={t("kpiServiceDrafts")}
                  value={serviceDrafts.data?.pagination.total ?? 0}
                  emphasis={
                    (serviceDrafts.data?.pagination.total ?? 0) > 0 ? "warn" : "neutral"
                  }
                  icon={IconFileText}
                  to="/contracts/services?status=DRAFT"
                />
                <KpiCard
                  label={t("kpiServicesEnding")}
                  value={servicesEnding.data?.pagination.total ?? 0}
                  emphasis={
                    (servicesEnding.data?.pagination.total ?? 0) > 0 ? "warn" : "neutral"
                  }
                  hint={t("kpiEndingHint")}
                  icon={IconFileText}
                  to="/contracts/services?endingWithinDays=30"
                />
              </>
            ) : null}
          </div>
        </section>
      ) : null}

      <div className="mt-6">
        <Card>
          <CardHeader
            title={t("devicesCard")}
            description={t("devicesCardHint")}
            action={
              <Link
                to="/devices"
                className="text-sm font-medium text-brand-strong underline underline-offset-2"
              >
                {t("seeAll")}
              </Link>
            }
          />
          <CardBody className="px-0 py-0">
            {devices.isPending ? <LoadingBlock label={t("loadingDevices")} /> : null}

            {devices.isError ? (
              <div className="p-4">
                <ErrorBlock error={devices.error} onRetry={() => void devices.refetch()} />
              </div>
            ) : null}

            {devices.data ? (
              <DataTable caption={t("devicesCaption")}>
                <thead>
                  <tr>
                    {DEVICE_TABLE_COLUMNS.map((column) => (
                      <Th key={column.label} numeric={column.numeric}>
                        {column.label}
                      </Th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <DeviceRows devices={devices.data.data.slice(0, 8)} />
                </tbody>
              </DataTable>
            ) : null}
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function RecentIncidents({ incidents }: { incidents: readonly DashboardIncident[] }) {
  const { t } = useTranslation("pages");

  return (
    <Card>
      <CardHeader
        title={t("recentIncidents")}
        description={t("recentIncidentsHint")}
        action={
          <Link
            to="/incidents"
            className="text-sm font-medium text-brand-strong underline underline-offset-2"
          >
            {t("seeAll")}
          </Link>
        }
      />
      <CardBody className="px-0 py-0">
        <DataTable caption={t("recentIncidentsCaption")}>
          <thead>
            <tr>
              <Th>{t("colAsset")}</Th>
              <Th>{t("colType")}</Th>
              <Th>{t("colSeverity")}</Th>
              <Th>{t("colStatus")}</Th>
              <Th numeric>{t("colValue")}</Th>
              <Th>{t("colStarted")}</Th>
            </tr>
          </thead>
          <tbody>
            {incidents.length === 0 ? (
              <EmptyRow colSpan={6}>{t("noIncidents")}</EmptyRow>
            ) : (
              incidents.map((incident) => (
                <tr key={incident.id}>
                  <Td>
                    <Link
                      to={`/devices/${incident.deviceId}`}
                      className="font-medium text-brand-strong underline underline-offset-2"
                    >
                      {incident.assetName ?? incident.devEui}
                    </Link>
                    <span className="block text-xs text-ink-muted">
                      {incident.siteName ?? t("noSite")}
                    </span>
                  </Td>
                  <Td>{INCIDENT_TYPE_LABELS[incident.type]}</Td>
                  <Td>
                    <SeverityBadge value={incident.severity} />
                  </Td>
                  <Td>
                    <IncidentStatusBadge value={incident.status} />
                  </Td>
                  <Td numeric>{formatIncidentValue(incident.type, incident.triggerValue)}</Td>
                  <Td>
                    <span title={formatDateTime(incident.startedAt)}>
                      {formatRelative(incident.startedAt)}
                    </span>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </DataTable>
      </CardBody>
    </Card>
  );
}
